<template>
  <div
    id="sidebar"
    @mouseenter="isBigScreen ? expand() : null"
    @mouseleave="isBigScreen ? collapse() : null"
    :style="inlineWidth"
    class="z-1000 flex flex-none flex-col justify-between bg-brand-blue-darker px-4 py-2 text-xs uppercase leading-normal tracking-widest text-white md:fixed md:inset-y-0 md:left-0 md:pt-8 md:pb-5">
    <header class="whitespace-nowrap">
      <div class="flex items-center justify-between">
        <a
          href="/"
          class="block text-white md:mx-auto">
          <Component
            :is="logo"
            class="h-6 w-auto"
            aria-hidden="true" />
          <span class="sr-only">Traxo</span>
        </a>

        <button
          type="button"
          v-if="!isBigScreen"
          @click="expanded ? collapse() : expand()"
          class="-mr-2 p-2">
          <Bars3Icon
            class="block h-6 w-6"
            v-if="!expanded" />
          <XMarkIcon
            class="block h-6 w-6"
            v-else />
        </button>
      </div>

      <div v-show="(expanded && !isBigScreen) || isBigScreen">
        <ul class="mt-6 md:mt-16">
          <li class="text-sm normal-case tracking-normal">
            <RouterLink
              :to="{ name: 'account.show' }"
              custom
              v-slot="{ href, navigate }">
              <a
                id="navAccount"
                :href="href"
                @click="navigate"
                class="block py-1 text-white hover:text-white hover:underline">
                <span ref="user">
                  <UserAvatar
                    :name="user.name"
                    :email="user.email"
                    :size="32"
                    class="mr-2 border border-white align-middle" />
                  <span :class="textClass"> {{ user.name }}</span>
                </span>
              </a>
            </RouterLink>
          </li>

          <li>
            <RouterLink
              :to="{ name: 'teams.index' }"
              custom
              v-slot="{ href, navigate, isActive }">
              <a
                id="navTeams"
                :href="href"
                @click="navigate"
                :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                class="-ml-4 -mr-3 flex items-center px-5 py-3">
                <BuildingOffice2Icon class="mr-3 h-6 w-6 shrink-0" />
                <span :class="textClass">Teams</span>
              </a>
            </RouterLink>
          </li>
        </ul>

        <nav
          v-if="team.id"
          class="mt-4 border-t border-dotted border-white px-1 pt-4">
          <!-- ORGS -->
          <ul v-if="teamType === 'org'">
            <li
              v-if="can('read-dashboard')"
              role="presentation"
              :class="{ 'text-sm normal-case tracking-normal': team.name }">
              <RouterLink
                :to="{ name: 'org.dashboard.index', params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navDashboard"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 block px-5 py-3">
                  <span ref="team">
                    <PresentationChartBarIcon class="mr-3 inline-block h-6 w-6 align-middle" />
                    <span :class="[textClass]">{{ team.name || 'Dashboard' }}</span>
                  </span>
                </a>
              </RouterLink>
            </li>
            <li
              v-if="can('browse-trips')"
              role="presentation">
              <RouterLink
                :to="{ name: 'org.travel.index', params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navTravel"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <TicketIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">Travel</span>
                </a>
              </RouterLink>
            </li>
            <li
              v-if="can('browse-reports')"
              role="presentation">
              <RouterLink
                :to="{ name: 'org.reports.index', params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navReports"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <ChartPieIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">Reports</span>
                </a>
              </RouterLink>
            </li>
            <li v-if="can('browse-members')">
              <RouterLink
                :to="{ name: 'org.members.index', params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navTravelers"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <UserGroupIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">People</span>
                </a>
              </RouterLink>
            </li>
            <li
              v-if="can('browse-emails')"
              role="presentation">
              <RouterLink
                :to="{ name: 'org.emails.index', params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navEmails"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <EnvelopeIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">Emails</span>
                </a>
              </RouterLink>
            </li>
            <li
              v-if="can('add-downloads')"
              role="presentation">
              <RouterLink
                :to="{
                  name: 'org.resources.index',
                  params: { teamId: team.id },
                }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navResources"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <BookOpenIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">Resources</span>
                </a>
              </RouterLink>
            </li>
            <li
              v-if="can('browse-settings')"
              role="presentation">
              <RouterLink
                :to="{ name: 'org.settings', params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navSettings"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <CogIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">Settings</span>
                </a>
              </RouterLink>
            </li>
          </ul>

          <!-- DEVS -->
          <ul v-if="teamType === 'dev'">
            <li
              v-if="can('read-dashboard')"
              role="presentation"
              class="text-sm normal-case tracking-normal">
              <RouterLink
                :to="{
                  name: 'dev.dashboard.index',
                  params: { teamId: team.id },
                }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navDashboard"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 block px-5 py-3">
                  <span ref="team">
                    <PresentationChartBarIcon class="mr-3 inline-block h-6 w-6 align-middle" />
                    <span :class="[textClass]">{{ team.name || 'Dashboard' }}</span>
                  </span>
                </a>
              </RouterLink>
            </li>
            <li
              v-if="can('browse-emails')"
              role="presentation">
              <RouterLink
                :to="{ name: 'dev.emails.index', params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navEmails"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <EnvelopeIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">Emails</span>
                </a>
              </RouterLink>
            </li>
            <li
              v-if="can('browse-mailboxes')"
              role="presentation">
              <RouterLink
                :to="{
                  name: 'dev.mailboxes.index',
                  params: { teamId: team.id },
                }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navMailboxes"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <InboxIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">Mailboxes</span>
                </a>
              </RouterLink>
            </li>
            <li
              v-if="can('browse-reports')"
              role="presentation">
              <RouterLink
                :to="{ name: 'dev.reports.index', params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navReports"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <ChartPieIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">Reports</span>
                </a>
              </RouterLink>
            </li>
            <li
              v-if="can('browse-settings')"
              role="presentation">
              <RouterLink
                :to="{ name: 'dev.settings', params: { teamId: team.id } }"
                custom
                v-slot="{ href, navigate, isActive }">
                <a
                  id="navSettings"
                  :href="href"
                  @click="navigate"
                  :class="isActive ? 'bg-white text-brand-blue-darker' : 'text-white hover:underline'"
                  class="-ml-5 -mr-4 flex items-center px-5 py-3">
                  <CogIcon class="mr-3 h-6 w-6 shrink-0" />
                  <span :class="textClass">Settings</span>
                </a>
              </RouterLink>
            </li>
          </ul>
        </nav>

        <div class="mt-4 border-t border-dotted border-white px-1 pt-4">
          <ul>
            <li role="presentation">
              <a
                href="/support"
                @click="handleRCTriggerClick"
                id="resource-center"
                class="-ml-5 -mr-4 flex items-center px-5 py-3 text-white hover:underline">
                <QuestionMarkCircleIcon class="mr-3 h-6 w-6 shrink-0" />
                <span :class="textClass">Support</span>
              </a>
            </li>

            <li role="presentation">
              <a
                id="navLogout"
                href="/logout"
                @click.prevent="logout"
                class="-ml-5 -mr-4 flex items-center px-5 py-3 text-white hover:underline">
                <ArrowLeftEndOnRectangleIcon class="mr-3 h-6 w-6 shrink-0" />
                <span :class="textClass">Log Out</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </header>

    <footer
      v-show="expanded && isBigScreen && !transitioning"
      class="px-1">
      <ul class="-ml-2 flex">
        <li>
          <a
            href="https://twitter.com/traxo"
            rel="noopener external"
            target="_blank"
            class="block p-2 text-white">
            <img
              src="@images/social-twitter-light.png"
              alt="Traxo on Twitter"
              class="w-4" />
          </a>
        </li>
        <li>
          <a
            href="https://www.facebook.com/Traxo"
            rel="noopener external"
            target="_blank"
            class="block p-2 text-white">
            <img
              src="@images/social-facebook-light.png"
              alt="Traxo on Facebook"
              class="w-4" />
          </a>
        </li>
        <li>
          <a
            href="https://www.linkedin.com/company/traxo"
            rel="noopener external"
            target="_blank"
            class="block p-2 text-white">
            <img
              src="@images/social-linkedin-light.png"
              alt="Traxo on Linkedin"
              class="w-4" />
          </a>
        </li>
      </ul>

      <p class="mt-2">&copy; {{ year() }} Traxo, Inc.<br />All Rights Reserved</p>
    </footer>
  </div>
</template>

<script setup>
import {
  PresentationChartBarIcon,
  TicketIcon,
  ChartPieIcon,
  UserGroupIcon,
  EnvelopeIcon,
  BookOpenIcon,
  CogIcon,
  BuildingOffice2Icon,
  QuestionMarkCircleIcon,
  ArrowLeftEndOnRectangleIcon,
  InboxIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import UserAvatar from '@/components/user-avatar.vue';
</script>

<script>
import { Tween, Easing } from '@tweenjs/tween.js';
import { http } from '@/utils/http.js';
import ApplicationLogo from '@/components/ApplicationLogo.vue';
import ApplicationLogoMark from '@/components/ApplicationLogoMark.vue';

const SIDEBAR_EXPANDED_MIN_WIDTH = 192;
const SIDEBAR_COLLAPSED_WIDTH = 64;
export default {
  data() {
    return {
      expanded: false,
      isBigScreen: false,
      // tween.js requires an object
      size: {
        width: SIDEBAR_COLLAPSED_WIDTH,
      },
      transitioning: false,
      tweenedSize: {},
    };
  },

  computed: {
    can() {
      return this.$store.getters['auth/can'];
    },

    user() {
      return this.$store.state.me.user;
    },

    team() {
      return this.$store.state.me.team;
    },

    teamType() {
      return this.$store.state.me.teamType;
    },

    inlineWidth() {
      if (!this.isBigScreen) {
        return null;
      }

      return { width: `${this.tweenedSize.width}px` };
    },

    textClass() {
      return {
        'md:invisible': this.expanded && this.transitioning,
        'md:hidden': !this.expanded,
      };
    },

    hasResourceCenter() {
      if (!Array.isArray(window.pendo?.guides)) {
        return false;
      }

      const rc = window.pendo.guides.filter((guide) => guide.name === 'Resource Center');

      return Boolean(rc.length);
    },

    logo() {
      if (!this.isBigScreen) {
        return ApplicationLogo;
      }

      if (this.expanded && !this.transitioning) {
        return ApplicationLogo;
      }

      return ApplicationLogoMark;
    },
  },

  watch: {
    size() {
      function animate() {
        requestAnimationFrame(animate);
        tween.update();
      }

      this.transitioning = true;
      let tween = new Tween(this.tweenedSize)
        .to(this.size, 150)
        .easing(Easing.Cubic.InOut)
        .onComplete(() => {
          this.transitioning = false;
        })
        .start();

      animate();
    },
  },

  created() {
    this.tweenedSize = { ...this.size };
  },

  mounted() {
    this.mediaQueryList = window.matchMedia('(min-width: 768px)');

    // initialize sidebar and screen width
    this.screenWidthListener(this.mediaQueryList);
    this.updateSidebarWidth();

    // set up media query listeners
    this.mediaQueryList.addListener(this.screenWidthListener);
  },

  beforeUnmount() {
    this.mediaQueryList.removeListener(this.screenWidthListener);
  },

  methods: {
    screenWidthListener(evt) {
      if (evt.matches) {
        this.isBigScreen = true;
      } else {
        this.isBigScreen = false;
      }
    },

    /**
     * Don't navigate to the support page if the pendo resource center exists
     * @param {Object} evt - browser event
     */
    handleRCTriggerClick(evt) {
      if (this.hasResourceCenter) {
        evt.preventDefault();
      }
    },

    /**
     * Change the sidebar width depending on the expanded/collapsed state
     * @returns {Undefined}
     */
    updateSidebarWidth() {
      // we don't care about widths on small screens
      if (!this.isBigScreen) {
        return;
      }

      if (this.expanded) {
        this.size = { width: this.calculateExpandedWidth() };
      } else {
        this.size = { width: SIDEBAR_COLLAPSED_WIDTH };
      }
    },

    /**
     * Figures out the width for the expanded sidebar depending on the largest width of the nav elements
     * @returns {Number}
     */
    calculateExpandedWidth() {
      let userNavWidth = this.getElementWidth(this.$refs.user);
      let teamNavWidth = this.getElementWidth(this.$refs.team);
      let largestNavWidth = userNavWidth > teamNavWidth ? userNavWidth : teamNavWidth;

      return largestNavWidth > SIDEBAR_EXPANDED_MIN_WIDTH ? largestNavWidth : SIDEBAR_EXPANDED_MIN_WIDTH;
    },

    /**
     * Calculates the elements width including sidebar padding.
     * @param {Object} rect - DOM element's BoundingClientRect object
     * @returns {Number}
     */
    getElementWidth(el) {
      if (!el) {
        return 0;
      }

      let rect = el.getBoundingClientRect();
      return Math.ceil(rect.left * 2 + window.pageXOffset + rect.width);
    },

    /**
     * Triggers sidebar expansion
     * @returns {Undefined}
     */
    expand() {
      if (this.expanded) {
        return;
      }

      this.expanded = true;

      // give this.textClass a chance to change so the
      // width is calculated correctly
      this.$nextTick(function () {
        this.updateSidebarWidth();
      });
    },

    /**
     * Triggers sidebar collapse
     * @returns {Undefined}
     */
    collapse() {
      if (!this.expanded) {
        return;
      }

      this.expanded = false;
      this.updateSidebarWidth();
    },

    logout() {
      // @TODO: Eventually we'll all routing through Vue and we'll be
      // able to handle this a bit differently
      http.post('/logout').then((response) => {
        this.$store.dispatch('resetStore');

        if (response.data && response.data.redirect) {
          window.location.assign(response.data.redirect);
        } else {
          window.location.assign('/');
        }
      });
    },

    year() {
      let now = new Date();
      return now.toLocaleDateString('en-US', { year: 'numeric' });
    },
  },
};
</script>
